import { useEffect, useState } from "react";
import { Plus, FileText, Check } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import { useQuote } from "@/context/QuoteContext";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { toast } from "@/components/ui/sonner";

// Square-tile quote picker dialog (方块选择格式)
export function QuotePicker({ open, onOpenChange, onSelect }) {
  const { t, lang } = useLanguage();
  const { activeQuote } = useQuote();
  const [quotes, setQuotes] = useState([]);
  const [creating, setCreating] = useState(false);

  const fmtDate = (dt) => (dt ? new Date(dt).toLocaleDateString(lang === "zh" ? "zh-CN" : "en-US") : "-");

  useEffect(() => {
    if (!open) return;
    (async () => {
      try {
        const { data } = await api.get("/quotes");
        setQuotes(data);
      } catch {
        setQuotes([]);
      }
    })();
  }, [open]);

  const handleNew = async () => {
    setCreating(true);
    try {
      const { data } = await api.post("/quotes", {});
      toast.success(`${t("quote.created")}: ${data.quote_number}`);
      onSelect(data);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail));
    } finally {
      setCreating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="quote-picker-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading text-base">{t("quote.pickQuote")}</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 max-h-[60vh] overflow-y-auto p-0.5" data-testid="quote-picker-grid">
          <button
            onClick={handleNew}
            disabled={creating}
            data-testid="quote-picker-new-tile"
            className="aspect-square rounded-md border-2 border-dashed border-border flex flex-col items-center justify-center gap-2 text-muted-foreground hover:border-brand hover:text-brand transition-colors"
          >
            <Plus size={24} />
            <span className="text-sm font-medium">{creating ? "..." : t("quote.new")}</span>
          </button>
          {quotes.map((q) => {
            const isActive = activeQuote?.id === q.id;
            return (
              <button
                key={q.id}
                onClick={() => onSelect(q)}
                data-testid={`quote-picker-tile-${q.quote_number}`}
                className={`relative aspect-square rounded-md border-2 p-3 flex flex-col items-start justify-between text-left transition-colors ${
                  isActive ? "border-brand bg-brand/5" : "border-border hover:border-brand/60"
                }`}
              >
                {isActive && (
                  <span className="absolute top-2 right-2 w-5 h-5 rounded-full bg-brand text-white flex items-center justify-center">
                    <Check size={12} />
                  </span>
                )}
                <FileText size={18} className={isActive ? "text-brand" : "text-muted-foreground"} />
                <div className="w-full min-w-0">
                  <p className="font-mono font-bold text-[13px] truncate">{q.quote_number}</p>
                  <p className="text-xs text-muted-foreground truncate">{q.buyer || t("quote.noBuyer")}</p>
                  <div className="flex items-center justify-between mt-1">
                    <span className="text-[11px] text-muted-foreground">{fmtDate(q.created_at)}</span>
                    <span className="font-mono text-[11px] text-muted-foreground">{q.items.length} {t("quote.items")}</span>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      </DialogContent>
    </Dialog>
  );
}
